import React, { useEffect, useMemo, useState } from "react";
import {
  Collection,
  createQuizShowRoom,
  fetchCollections,
  getWebSocketBaseUrl,
} from "../../api";
import { useAuth0 } from "@auth0/auth0-react";
import { tourStepsQuizShow } from "./tourStepsQuizShow";
import GuidedTour from "../../components/GuidedTour";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";
import {
  CollectionItemData,
  parseCollectionItems,
} from "../../utils/parseCollectionItems";
import MultiplayerHostDisplay from "../../components/MultiplayerHostDisplay";
import { useWebSocket } from "../../hooks/useWebSocket";
import {
  QuizShowQuestion,
  parseQuizShowState,
  shuffleItems,
} from "./quizShowShared";

type QuestionSource = "collection" | "custom" | "sample";

interface CustomQuestionDraft {
  prompt: string;
  answers: string[];
  correctIndex: number;
}

const SAMPLE_QUESTIONS: QuizShowQuestion[] = [
  {
    prompt: "How many sides does a triangle have?",
    choices: ["2", "3", "4", "5"],
    correctIndex: 1,
  },
  {
    prompt: "Which letter comes after A?",
    choices: ["C", "B", "Z", "D"],
    correctIndex: 1,
  },
  {
    prompt: "What color do you get if you mix blue and yellow?",
    choices: ["Green", "Purple", "Orange", "Brown"],
    correctIndex: 0,
  },
  {
    prompt: "How many days are in a week?",
    choices: ["5", "6", "7", "10"],
    correctIndex: 2,
  },
];

const CUSTOM_STORAGE_KEY = "multipleChoiceQuestions";

const getCollectionItems = (collection: Collection): CollectionItemData[] => {
  const fromDescription = parseCollectionItems(collection.description);
  if (fromDescription.length > 0) {
    return fromDescription;
  }
  return (collection.items ?? []).map((item) => ({
    name: item.name,
    svg: item.svg,
    count: item.count,
  }));
};

const questionsFromCollection = (
  items: CollectionItemData[],
): QuizShowQuestion[] => {
  const usable = items.filter((item) => item.name.trim().length > 0);
  if (usable.length < 2) {
    return [];
  }
  return shuffleItems(usable).map((item) => {
    const decoys = shuffleItems(
      usable.filter((other) => other.name !== item.name),
    ).slice(0, 3);
    const options = shuffleItems([item, ...decoys]);
    return {
      prompt: item.svg ? "What is this?" : item.name,
      imageUrl: item.svg,
      choices: options.map((option) => option.name),
      correctIndex: Math.max(
        0,
        options.findIndex((option) => option.name === item.name),
      ),
    };
  });
};

const loadSavedCustomQuestions = (): QuizShowQuestion[] => {
  try {
    const raw = localStorage.getItem(CUSTOM_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((entry) => {
        const record = entry as CustomQuestionDraft;
        if (
          !record.prompt ||
          !Array.isArray(record.answers) ||
          record.answers.length < 2
        ) {
          return null;
        }
        return {
          prompt: record.prompt,
          choices: record.answers,
          correctIndex: record.correctIndex ?? 0,
        } satisfies QuizShowQuestion;
      })
      .filter((question): question is QuizShowQuestion => question !== null);
  } catch {
    return [];
  }
};

const QuizShow: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState<QuestionSource>("sample");
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    number | null
  >(null);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [hostToken, setHostToken] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuizShowQuestion[]>([]);
  const [error, setError] = useState("");

  const savedCustomQuestions = useMemo(() => loadSavedCustomQuestions(), []);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted_quizShow");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchCollections(getAccessTokenSilently);
        setCollections(Array.isArray(data) ? data : []);
      } catch (loadError) {
        console.error("Failed to load collections", loadError);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [getAccessTokenSilently]);

  const selectedCollection = collections.find(
    (collection) => collection.collection_id === selectedCollectionId,
  );
  const collectionQuestions = selectedCollection
    ? questionsFromCollection(getCollectionItems(selectedCollection))
    : [];

  const canStart =
    (source === "sample" && SAMPLE_QUESTIONS.length > 0) ||
    (source === "custom" && savedCustomQuestions.length > 0) ||
    (source === "collection" && collectionQuestions.length > 0);

  const wsUrl =
    roomCode && hostToken
      ? `${getWebSocketBaseUrl()}/ws/quiz-show/${roomCode}?role=host&token=${encodeURIComponent(hostToken)}`
      : null;
  const { send, lastMessage, isOpen } = useWebSocket(wsUrl);
  const state = parseQuizShowState(lastMessage);
  const joinUrl = roomCode
    ? `${window.location.origin}/join/${roomCode}`
    : "";

  const handleCreateRoom = async () => {
    setError("");
    let nextQuestions: QuizShowQuestion[] = SAMPLE_QUESTIONS;
    if (source === "collection") {
      nextQuestions = collectionQuestions;
    } else if (source === "custom") {
      nextQuestions = savedCustomQuestions;
    }
    if (nextQuestions.length === 0) {
      setError("Add questions or pick a collection with at least two items.");
      return;
    }
    try {
      const room = await createQuizShowRoom(
        getAccessTokenSilently,
        source === "collection" ? selectedCollectionId ?? undefined : undefined,
      );
      setQuestions(nextQuestions);
      setRoomCode(room.roomCode);
      setHostToken(room.hostToken);
    } catch (createError) {
      console.error(createError);
      setError("Could not create a room. Try again.");
    }
  };

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={() => navigate("/games")} />
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Quiz Show</BubbleText>
      </h1>

      <div className="game-instructions flex w-full max-w-3xl flex-col items-center">
        <p className="inherit mb-6 text-center">
          Put the question on the class screen. Students join on their phones
          with a room code and tap an answer. No student login needed.
        </p>

        <div className="question-source mb-6 w-full space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="quiz-source"
              checked={source === "sample"}
              onChange={() => setSource("sample")}
            />
            Sample questions
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="quiz-source"
              checked={source === "collection"}
              onChange={() => setSource("collection")}
            />
            Use a collection
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="quiz-source"
              checked={source === "custom"}
              onChange={() => setSource("custom")}
            />
            Questions from Multiple Choice ({savedCustomQuestions.length})
          </label>
        </div>

        {source === "collection" && (
          <div className="mb-6 w-full">
            {isLoading ? (
              <p>Loading collections...</p>
            ) : collections.length === 0 ? (
              <p>Create a collection first, then come back.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {collections.map((collection) => {
                  const itemCount = getCollectionItems(collection).length;
                  const isSelected =
                    collection.collection_id === selectedCollectionId;
                  return (
                    <button
                      key={collection.collection_id}
                      type="button"
                      onClick={() =>
                        setSelectedCollectionId(collection.collection_id)
                      }
                      className={`rounded border-2 p-3 text-left ${
                        isSelected
                          ? "border-blue-600 bg-blue-100 text-black"
                          : "border-black bg-white text-black hover:border-blue-400"
                      }`}
                    >
                      <p className="font-semibold">{collection.name}</p>
                      <p className="text-sm">
                        {itemCount} {itemCount === 1 ? "item" : "items"}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {error && <p className="mb-4 text-red-600">{error}</p>}

        <button
          type="button"
          onClick={handleCreateRoom}
          disabled={!canStart}
          className={`start-button max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white hover:bg-green-600 ${
            canStart ? "cursor-pointer" : "cursor-not-allowed opacity-50"
          }`}
        >
          Create Room
        </button>
      </div>

      {roomCode && hostToken && (
        <MultiplayerHostDisplay
          roomCode={roomCode}
          joinUrl={joinUrl}
          questions={questions}
          state={state}
          isOpen={isOpen}
          send={send}
          onLeave={() => {
            send({ type: "end" });
            setRoomCode(null);
            setHostToken(null);
          }}
        />
      )}

      <GuidedTour
        steps={tourStepsQuizShow()}
        isRunning={isTourRunning}
        onComplete={() => {
          setIsTourRunning(false);
          localStorage.setItem("tourCompleted_quizShow", "true");
        }}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="quizShow"
      />
    </div>
  );
};

export default QuizShow;
